'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { BookmarkAdd01Icon, Add01Icon } from '@terra/ui-icons';
import { Popover } from '../../../components/popover';
import { Button } from '../../../components/button';
import { Textarea } from '../../../components/textarea';
import { Text } from '../../../components/styled/text';
import { Tooltip } from '../../../components/tooltip';
import { toasterApi } from '../../toast/ToastProvider';
import { useAddToEvidence } from '../../chat/trace-view/AddToEvidenceProvider';

type AddToEvidenceButtonProps = {
  artifactId: string | undefined;
  label?: string;
  isEvidence?: boolean;
};

export function AddToEvidenceButton({
  artifactId,
  label,
  isEvidence,
}: AddToEvidenceButtonProps) {
  const addToEvidence = useAddToEvidence();
  const tCommon = useTranslations('designSystem.common');
  const tEvidence = useTranslations('designSystem.artifacts.addToEvidence');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasSubmittedEmptyDescription, setHasSubmittedEmptyDescription] =
    useState(false);

  if (!addToEvidence?.add || !artifactId || isEvidence) {
    return null;
  }

  const handleSubmit = async () => {
    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setHasSubmittedEmptyDescription(true);

      return;
    }

    setIsSubmitting(true);

    try {
      await addToEvidence.add(artifactId, trimmedDescription);
      toasterApi.success({ title: tEvidence('added') });
      setDescription('');
      setHasSubmittedEmptyDescription(false);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to add evidence', error);
      toasterApi.error({ title: tEvidence('addFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setDescription(event.target.value);
    if (event.target.value.trim()) {
      setHasSubmittedEmptyDescription(false);
    }
  };

  const handleDescriptionKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      handleSubmit();
    }
  };

  const trigger = label ? (
    <HStack
      as="button"
      aria-label={label}
      h="5"
      px={1}
      gap={1}
      alignItems="center"
      cursor="pointer"
      color="fg.muted"
      borderRadius="xs"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      _hover={{
        color: 'fg.default',
        bg: 'bg.subtle',
      }}
    >
      <Add01Icon size={12} />
      <Text fontSize="xs" fontWeight="normal">
        {label}
      </Text>
    </HStack>
  ) : (
    <Box
      as="button"
      aria-label={tEvidence('title')}
      h="5"
      w="5"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      color="fg.muted"
      borderRadius="xs"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      _hover={{
        color: 'fg.default',
        bg: 'bg.subtle',
      }}
    >
      <BookmarkAdd01Icon size={12} />
    </Box>
  );

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open);
        if (!open) {
          setDescription('');
          setHasSubmittedEmptyDescription(false);
        }
      }}
      positioning={{ placement: 'bottom-end' }}
    >
      {label ? (
        <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      ) : !isOpen ? (
        <Tooltip title={tEvidence('title')}>
          <Popover.Trigger asChild>{trigger}</Popover.Trigger>
        </Tooltip>
      ) : (
        <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      )}
      <Popover.Positioner>
        <Popover.Content onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <VStack gap={3} p={3} alignItems="stretch" w="320px">
            <VStack gap={1} alignItems="stretch">
              <Text fontSize="sm" fontWeight="medium" color="fg.default">
                {tEvidence('title')}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {tEvidence('description')}
              </Text>
            </VStack>
            <VStack gap={1} alignItems="stretch">
              <Textarea
                placeholder={tEvidence('placeholder')}
                value={description}
                onChange={handleDescriptionChange}
                onKeyDown={handleDescriptionKeyDown}
                autoFocus
                rows={4}
              />
              {hasSubmittedEmptyDescription && (
                <Text fontSize="xs" color="fg.error">
                  {tEvidence('descriptionRequired')}
                </Text>
              )}
            </VStack>
            <HStack gap={2} justifyContent="flex-end">
              <Popover.CloseTrigger asChild>
                <Button size="xs" variant="ghost">
                  {tCommon('cancel')}
                </Button>
              </Popover.CloseTrigger>
              <Button
                size="xs"
                variant="solid"
                onClick={handleSubmit}
                disabled={!description.trim() || isSubmitting}
                loading={isSubmitting}
              >
                {tCommon('add')}
              </Button>
            </HStack>
          </VStack>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
